/**
 * Performance Monitoring Service
 * Tracks application performance metrics and sends them to monitoring services
 */

// Performance metrics storage
let metrics = {
  pageLoads: [],
  apiCalls: [],
  errors: [],
  userInteractions: [],
  resourceTiming: [],
  vitals: {}
};

// Configuration
const config = {
  enabled: false, // Disabled - backend endpoint not implemented
  sampleRate: 0.1, // Sample 10% of users
  maxMetrics: 1000, // Maximum metrics to store before sending
  endpoint: '/api/v1/analytics/performance',
  flushInterval: 30000, // Send metrics every 30 seconds
};

// Performance observer instances
let performanceObserver = null;
let vitalsObserver = null;

/**
 * Initialize performance monitoring
 */
export const initPerformanceMonitoring = () => {
  if (!config.enabled) return;

  // Set up performance observer for various metrics
  setupPerformanceObserver();
  setupWebVitalsObserver();
  setupErrorTracking();
  setupApiTracking();
  
  // Start periodic flush
  setInterval(flushMetrics, config.flushInterval);
  
  // Track page load
  trackPageLoad();
};

/**
 * Setup Performance Observer API
 */
const setupPerformanceObserver = () => {
  if (!window.PerformanceObserver) return;

  try {
    performanceObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.entryType === 'navigation') {
          trackNavigationTiming(entry);
        } else if (entry.entryType === 'resource') {
          trackResourceTiming(entry);
        } else if (entry.entryType === 'measure') {
          trackCustomMeasure(entry);
        }
      });
    });

    performanceObserver.observe({ 
      entryTypes: ['navigation', 'resource', 'measure'] 
    });
  } catch (error) {
    console.warn('Performance Observer not supported:', error);
  }
};

/**
 * Setup Web Vitals monitoring
 */
const setupWebVitalsObserver = () => {
  // Track Core Web Vitals
  if ('PerformanceObserver' in window) {
    try {
      vitalsObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.entryType === 'largest-contentful-paint') {
            metrics.vitals.lcp = entry.startTime;
          } else if (entry.entryType === 'first-input') {
            metrics.vitals.fid = entry.processingStart - entry.startTime;
          } else if (entry.entryType === 'layout-shift') {
            if (!entry.hadRecentInput) {
              metrics.vitals.cls = (metrics.vitals.cls || 0) + entry.value;
            }
          }
        });
      });

      vitalsObserver.observe({ 
        entryTypes: ['largest-contentful-paint', 'first-input', 'layout-shift'] 
      });
    } catch (error) {
      console.warn('Web Vitals Observer not supported:', error);
    }
  }

  // Track First Contentful Paint
  new PerformanceObserver((list) => {
    list.getEntries().forEach((entry) => {
      if (entry.name === 'first-contentful-paint') {
        metrics.vitals.fcp = entry.startTime;
      }
    });
  }).observe({ entryTypes: ['paint'] });
};

/**
 * Setup error tracking
 */
const setupErrorTracking = () => {
  // Track JavaScript errors
  window.addEventListener('error', (event) => {
    trackError({
      type: 'javascript',
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack,
      timestamp: Date.now()
    });
  });

  // Track unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    trackError({
      type: 'promise',
      message: event.reason?.message || 'Unhandled promise rejection',
      stack: event.reason?.stack,
      timestamp: Date.now()
    });
  });
};

/**
 * Setup API call tracking
 */
const setupApiTracking = () => {
  // This would be integrated with the API service
  // For now, we'll track fetch calls
  const originalFetch = window.fetch;
  
  window.fetch = async (...args) => {
    const start = performance.now();
    const url = args[0];
    const method = args[1]?.method || 'GET';
    
    try {
      const response = await originalFetch(...args);
      const duration = performance.now() - start;
      
      trackApiCall({
        url,
        method,
        status: response.status,
        duration,
        success: response.ok,
        timestamp: Date.now()
      });
      
      return response;
    } catch (error) {
      const duration = performance.now() - start;
      
      trackApiCall({
        url,
        method,
        status: 0,
        duration,
        success: false,
        error: error.message,
        timestamp: Date.now()
      });
      
      throw error;
    }
  };
};

/**
 * Track page load performance
 */
const trackPageLoad = () => {
  if ('performance' in window && 'timing' in window.performance) {
    const timing = window.performance.timing;
    const navigation = window.performance.navigation;
    
    const pageLoad = {
      type: 'page_load',
      url: window.location.href,
      timestamp: Date.now(),
      dns: timing.domainLookupEnd - timing.domainLookupStart,
      tcp: timing.connectEnd - timing.connectStart,
      ssl: timing.secureConnectionStart > 0 ? timing.connectEnd - timing.secureConnectionStart : 0,
      ttfb: timing.responseStart - timing.navigationStart,
      response: timing.responseEnd - timing.responseStart,
      dom: timing.domContentLoadedEventStart - timing.responseEnd,
      load: timing.loadEventEnd - timing.domContentLoadedEventStart,
      total: timing.loadEventEnd - timing.navigationStart,
      navigationType: navigation.type,
      redirectCount: navigation.redirectCount
    };
    
    metrics.pageLoads.push(pageLoad);
  }
};

/**
 * Track navigation timing
 */
const trackNavigationTiming = (entry) => {
  // Already handled by trackPageLoad for initial load
  if (entry.name === 'document') return;
  
  const navigation = {
    type: 'navigation',
    url: entry.name,
    timestamp: Date.now(),
    duration: entry.duration,
    startTime: entry.startTime,
    transferSize: entry.transferSize,
    encodedBodySize: entry.encodedBodySize,
    decodedBodySize: entry.decodedBodySize
  };
  
  metrics.pageLoads.push(navigation);
};

/**
 * Track resource loading timing
 */
const trackResourceTiming = (entry) => {
  const resource = {
    type: 'resource',
    name: entry.name,
    initiatorType: entry.initiatorType,
    startTime: entry.startTime,
    duration: entry.duration,
    transferSize: entry.transferSize,
    encodedBodySize: entry.encodedBodySize,
    decodedBodySize: entry.decodedBodySize,
    timestamp: Date.now()
  };
  
  metrics.resourceTiming.push(resource);
  
  // Limit resource metrics to prevent memory issues
  if (metrics.resourceTiming.length > 100) {
    metrics.resourceTiming = metrics.resourceTiming.slice(-50);
  }
};

/**
 * Track custom performance measures
 */
const trackCustomMeasure = (entry) => {
  const measure = {
    type: 'measure',
    name: entry.name,
    duration: entry.duration,
    startTime: entry.startTime,
    timestamp: Date.now()
  };
  
  metrics.userInteractions.push(measure);
};

/**
 * Track API call performance
 */
export const trackApiCall = (apiData) => {
  if (!config.enabled) return;
  
  metrics.apiCalls.push(apiData);
  
  // Limit API metrics
  if (metrics.apiCalls.length > 100) {
    metrics.apiCalls = metrics.apiCalls.slice(-50);
  }
};

/**
 * Track user interaction
 */
export const trackUserInteraction = (name, duration, metadata = {}) => {
  if (!config.enabled) return;
  
  const interaction = {
    type: 'user_interaction',
    name,
    duration,
    metadata,
    timestamp: Date.now()
  };
  
  metrics.userInteractions.push(interaction);
};

/**
 * Track errors
 */
export const trackError = (errorData) => {
  if (!config.enabled) return;
  
  metrics.errors.push(errorData);
  
  // Limit error metrics
  if (metrics.errors.length > 50) {
    metrics.errors = metrics.errors.slice(-25);
  }
};

/**
 * Start a custom performance measure
 */
export const startMeasure = (name) => {
  if (!config.enabled) return;
  
  performance.mark(`${name}-start`);
};

/**
 * End a custom performance measure
 */
export const endMeasure = (name) => {
  if (!config.enabled) return;
  
  try {
    performance.mark(`${name}-end`);
    performance.measure(name, `${name}-start`, `${name}-end`);
  } catch (error) {
    console.warn('Performance measure failed:', error);
  }
};

/**
 * Measure function execution time
 */
export const measureFunction = (name, fn) => {
  if (!config.enabled) return fn;
  
  return async (...args) => {
    startMeasure(name);
    try {
      const result = await fn(...args);
      endMeasure(name);
      return result;
    } catch (error) {
      endMeasure(name);
      throw error;
    }
  };
};

/**
 * Get current metrics summary
 */
export const getMetricsSummary = () => {
  return {
    pageLoads: metrics.pageLoads.length,
    apiCalls: metrics.apiCalls.length,
    errors: metrics.errors.length,
    userInteractions: metrics.userInteractions.length,
    vitals: metrics.vitals,
    timestamp: Date.now()
  };
};

/**
 * Flush metrics to server
 */
const flushMetrics = async () => {
  if (!config.enabled || !shouldSample()) return;
  
  const metricsToSend = {
    ...metrics,
    userAgent: navigator.userAgent,
    url: window.location.href,
    timestamp: Date.now()
  };
  
  try {
    await fetch(config.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(metricsToSend)
    });
    
    // Clear sent metrics
    metrics = {
      pageLoads: [],
      apiCalls: [],
      errors: [],
      userInteractions: [],
      resourceTiming: [],
      vitals: {}
    };
  } catch (error) {
    console.warn('Failed to send performance metrics:', error);
  }
};

/**
 * Check if we should sample this user
 */
const shouldSample = () => {
  return Math.random() < config.sampleRate;
};

/**
 * Get performance grade
 */
export const getPerformanceGrade = () => {
  const vitals = metrics.vitals;
  
  if (!vitals.lcp || !vitals.fid || !vitals.cls) {
    return 'unknown';
  }
  
  let score = 0;
  
  // LCP scoring (good < 2.5s)
  if (vitals.lcp < 2500) score += 33;
  else if (vitals.lcp < 4000) score += 20;
  
  // FID scoring (good < 100ms)
  if (vitals.fid < 100) score += 33;
  else if (vitals.fid < 300) score += 20;
  
  // CLS scoring (good < 0.1)
  if (vitals.cls < 0.1) score += 34;
  else if (vitals.cls < 0.25) score += 20;
  
  if (score >= 80) return 'excellent';
  if (score >= 60) return 'good';
  if (score >= 40) return 'needs-improvement';
  return 'poor';
};

/**
 * Export metrics for debugging
 */
export const exportMetrics = () => {
  return JSON.stringify(metrics, null, 2);
};

// Auto-initialize if enabled
if (config.enabled) {
  initPerformanceMonitoring();
}

const performanceService = {
  initPerformanceMonitoring,
  trackApiCall,
  trackUserInteraction,
  trackError,
  startMeasure,
  endMeasure,
  measureFunction,
  getMetricsSummary,
  getPerformanceGrade,
  exportMetrics
};

export default performanceService;
